/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types';
import _ from 'lodash';
import moment from 'moment-timezone';
import FormGroup from '@material-ui/core/FormGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { KeyboardTimePicker } from '@material-ui/pickers';
import { Schedule as ScheduleIcon } from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import useEffectSkipFirst from '../../../utilities/UseEffectSkipFirst';
import DATE_FORMATS from '../../../utilities/DateTimeFormats';
import useStyles from './ReportDialogStyles';

export const INTERVALS = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
};

export const INTERVAL_LABELS = {
    [INTERVALS.DAILY]: t`Daily`,
    [INTERVALS.WEEKLY]: t`Weekly`,
    [INTERVALS.MONTHLY]: t`Monthly`,
};

const DAYS = {
    MON: {
        value: 1,
        label: t`Mon`,
    },
    TUE: {
        value: 2,
        label: t`Tue`,
    },
    WED: {
        value: 3,
        label: t`Wed`,
    },
    THU: {
        value: 4,
        label: t`Thur`,
    },
    FRI: {
        value: 5,
        label: t`Fri`,
    },
    SAT: {
        value: 6,
        label: t`Sat`,
    },
    SUN: {
        value: 7,
        label: t`Sun`,
    },
};

export const recurrenceOptionsType = shape({
    interval: oneOf(Object.values(INTERVALS)),
    days: arrayOf(number),
    time: string,
    timezone: string,
});

export const defaultRecurrenceOptions = {
    interval: INTERVALS.DAILY,
};

function momentFromTime(time, timezone) {
    if (!time) {
        // Create a new time rounded to the nearest future 15 minute mark.
        const newTime = moment().tz(timezone);
        const minutes = newTime.minutes();
        newTime.minutes(minutes + (15 - (minutes % 15)));
        return newTime;
    }
    const [hours, minutes] = time.split(':');
    return moment().hours(parseInt(hours, 10)).minutes(parseInt(minutes, 10));
}

function dayOfMonthFromDays(days) {
    if (!days || days.length === 0) return moment().date().toString();
    return days[0].toString();
}

export default function RecurrenceOptions({ options, isNew, onChange, onValidate }) {
    const classes = useStyles();
    const [interval, setInterval] = useState(options.interval || INTERVALS.DAILY);
    const [days, setDays] = useState(options.days || []);
    const [dayOfMonth, setDayOfMonth] = useState(dayOfMonthFromDays(options.days));
    const daysOfMonth = useMemo(() => _.range(1, 31).map((d) => d.toString()), []);
    const [timezone, setTimezone] = useState(options.timezone);
    const [time, setTime] = useState(momentFromTime(options.time, timezone));
    const [timeErrorMessage, setTimeErrorMessage] = useState(undefined);
    const [timeValid, setTimeValid] = useState(true);
    const timeZones = useMemo(() => moment.tz.names(), []);
    const [showDays, showDayOfMonth] = useMemo(
        () => [interval === INTERVALS.WEEKLY, interval === INTERVALS.MONTHLY],
        [interval]
    );
    const daysValid = useMemo(() => !showDays || days.length > 0, [showDays, days]);

    // Skip the first change, since it is always undefined.  This ensures that the
    // field is not marked invalid until it is changed or blurred.
    useEffectSkipFirst(
        () => {
            const valid = !!time && time.isValid();
            setTimeValid(valid);
            setTimeErrorMessage(valid ? undefined : i18n._(t`A time in 12-hour format is required`));
        },
        [time],
        isNew
    );

    useEffect(() => {
        onValidate(timeValid && daysValid);
        onChange({
            interval,
            days,
            time: time?.format(DATE_FORMATS.TIME_MIN_24H) || '',
            timezone,
        });
        // We don't need the onChange or onValidate dependencies and adding them can cause excessive updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interval, days, time, timezone, timeValid, daysValid]);

    function handleIntervalChange(event) {
        const newInterval = event.target.value;

        if (newInterval === INTERVALS.MONTHLY) {
            const today = dayOfMonthFromDays([]);
            setDayOfMonth(today);
            setDays([parseInt(today, 10)]);
        } else if (newInterval === INTERVALS.WEEKLY) {
            setDays([]);
        }

        setInterval(newInterval);
    }

    function handleDaySelected(event, day) {
        const selected = event.target.checked;
        let newDays;

        if (selected) {
            newDays = [...days, day.value];
        } else {
            newDays = _.without(days, day.value);
        }

        setDays(newDays);
    }

    function handleDayOfMonthChange(event, newDayOfMonth) {
        const newDays = [parseInt(newDayOfMonth, 10)];
        setDayOfMonth(newDayOfMonth);
        setDays(newDays);
    }

    return (
        <div className={classes.recurrenceOptions}>
            <FormGroup row>
                <FormControl>
                    <InputLabel id="report-recurrence-interval-label">
                        <Trans>Recurrence</Trans>
                    </InputLabel>
                    <Select
                        id="report-recurrence-interval"
                        labelId="report-recurrence-interval-label"
                        value={interval}
                        onChange={handleIntervalChange}
                    >
                        {_.map(INTERVALS, (value) => (
                            <MenuItem key={value} value={value}>
                                {i18n._(INTERVAL_LABELS[value])}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {showDays
                    ? _.map(DAYS, (day) => (
                          <FormControlLabel
                              key={day.value}
                              id={`report-recurrence-day-of-week-${day.value}`}
                              label={i18n._(day.label)}
                              control={
                                  <Checkbox
                                      checked={days.includes(day.value)}
                                      onChange={(event) => handleDaySelected(event, day)}
                                  />
                              }
                          />
                      ))
                    : null}
                {showDayOfMonth ? (
                    <Autocomplete
                        id="report-recurrence-day-of-month"
                        className={classes.dayOfMonth}
                        autoHighlight
                        autoSelect
                        disableClearable
                        options={daysOfMonth}
                        value={dayOfMonth}
                        renderInput={(params) => <TextField {...params} label={<Trans>Day of Month</Trans>} />}
                        onChange={handleDayOfMonthChange}
                    />
                ) : null}
            </FormGroup>
            <FormGroup row>
                <KeyboardTimePicker
                    id="report-recurrence-time"
                    label={<Trans>Time</Trans>}
                    variant="inline"
                    maskChar="-"
                    mask="--:-- -M"
                    invalidDateMessage={timeErrorMessage}
                    error={!!timeErrorMessage}
                    minutesStep={5}
                    autoOk
                    required
                    keyboardIcon={<ScheduleIcon />}
                    value={time}
                    onChange={setTime}
                />
                <Autocomplete
                    id="report-recurrence-time-zone"
                    className={classes.timeZone}
                    autoHighlight
                    autoSelect
                    disableClearable
                    options={timeZones}
                    value={timezone}
                    renderInput={(params) => <TextField {...params} label={<Trans>Time Zone</Trans>} />}
                    onChange={(event, tz) => setTimezone(tz)}
                />
            </FormGroup>
        </div>
    );
}

RecurrenceOptions.propTypes = {
    options: recurrenceOptionsType.isRequired,
    isNew: bool.isRequired,
    onChange: func.isRequired,
    onValidate: func.isRequired,
};
